import { Link, useLocation, useNavigate } from "react-router-dom";
import { Linkedin } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";
  const { user } = useAuth();
  const canAccessQuestionnaire = user?.role === "travel_partner";

  const quickLinks = [
    { name: "Home", href: "#home" },
    { name: "About Certification", href: "#about" },
    { name: "Property Methodology", href: "/methodology", isRoute: true },
    { name: "Destination Methodology", href: "/methodology/destinations", isRoute: true },
    ...(canAccessQuestionnaire
      ? [{ name: "Property Questionnaire", href: "/questionnaire", isRoute: true }]
      : []),
    { name: "Quality Directory", href: "#directory" },
    { name: "Apply for Certification", href: "#apply" }
  ];

  const certificationTypes = [
    {
      name: "Gold Certificate",
      tier: "gold",
      logo: "https://customer-assets.emergentagent.com/job_travel-quality-label/artifacts/3qf4e4f0_Black%20And%20White%20Simple%20Icon%20Logo.png",
      color: "#D4AF37",
    },
    {
      name: "Silver Certificate",
      tier: "silver",
      logo: "https://customer-assets.emergentagent.com/job_travel-quality-label/artifacts/80ndwcjh_Black%20And%20White%20Simple%20Icon%20Logo%20%281%29.png",
      color: "#C0C0C0",
    },
    {
      name: "Bronze Certificate",
      tier: "bronze",
      logo: "https://customer-assets.emergentagent.com/job_travel-quality-label/artifacts/o5vrg8qt_Black%20And%20White%20Simple%20Icon%20Logo%20%282%29.png",
      color: "#CD7F32",
    },
  ];

  const scrollToSection = (e, href) => {
    e.preventDefault();
    if (!isHome) {
      navigate("/");
      setTimeout(() => {
        const el = document.querySelector(href);
        if (el) el.scrollIntoView({ behavior: "smooth" });
        else window.scrollTo({ top: 0, behavior: "smooth" });
      }, 80);
      return;
    }
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <footer 
      className="bg-[#0B132B] text-white"
      data-testid="footer"
    >
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <a 
              href="#home" 
              onClick={(e) => scrollToSection(e, "#home")}
              className="flex items-center mb-6"
              data-testid="footer-logo"
            >
              <span className="font-heading text-2xl font-bold tracking-tight">
                <span className="text-white">WTT</span>
                <span className="text-[#D4AF37]">Q</span>
                <span className="text-white">.org</span>
              </span>
            </a>
            <p className="text-white/70 text-sm leading-relaxed mb-6">
              World Travel & Tourism Quality Organization. The global standard for 
              hospitality excellence and tourism certification.
            </p>
            {/* WTTQ Alliance Logo */}
            <Link
              to="/alliance"
              className="inline-flex items-center gap-3 mb-6 group"
              data-testid="footer-alliance-logo"
              aria-label="WTTQ Global Alliance"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_travel-quality-label/artifacts/1r7l2mjv_Quality%20Alliance%20%284%29.png"
                alt="WTTQ Quality Alliance"
                className="w-14 h-14 rounded-md ring-1 ring-[#D4AF37]/40 group-hover:ring-[#D4AF37] transition"
              />
              <span className="text-xs tracking-[0.3em] uppercase text-white/70 group-hover:text-[#D4AF37] transition-colors">
                Global Alliance
              </span>
            </Link>
            {/* Social Links */}
            <div className="flex gap-4">
              <a 
                href="https://www.linkedin.com/company/wttq" 
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-[#D4AF37] hover:text-[#0B132B] transition-colors"
                data-testid="social-linkedin"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading text-lg font-bold text-white mb-6">
              Quick Links
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  {link.isRoute ? (
                    <Link
                      to={link.href}
                      className="text-white/70 hover:text-[#D4AF37] text-sm transition-colors"
                      data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {link.name}
                    </Link>
                  ) : (
                    <a
                      href={link.href}
                      onClick={(e) => scrollToSection(e, link.href)}
                      className="text-white/70 hover:text-[#D4AF37] text-sm transition-colors"
                      data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {link.name}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Certification Types */}
          <div>
            <h4 className="font-heading text-lg font-bold text-white mb-6">
              Certifications
            </h4>
            <ul className="space-y-3">
              {certificationTypes.map((c) => (
                <li key={c.name}>
                  <Link
                    to="/methodology#certification-levels"
                    className="group inline-flex items-center gap-3 text-sm transition-colors"
                    style={{ color: c.color }}
                    data-testid={`footer-cert-${c.tier}`}
                  >
                    <img
                      src={c.logo}
                      alt={`${c.name} badge`}
                      className="w-7 h-7 rounded-sm ring-1 ring-white/10 group-hover:ring-white/40 transition"
                    />
                    <span className="group-hover:underline underline-offset-4">{c.name}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-white/50 text-sm text-center md:text-left">
              &copy; {currentYear} WTTQ.Org. All rights reserved.
            </p>
            <div className="flex gap-6">
              <a 
                href="#" 
                className="text-white/50 hover:text-white/80 text-sm transition-colors"
                data-testid="footer-privacy"
              >
                Privacy Policy
              </a>
              <a 
                href="#" 
                className="text-white/50 hover:text-white/80 text-sm transition-colors"
                data-testid="footer-terms"
              >
                Terms of Service
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
